/**
 * Feature Flags Constants
 *
 * Single source of truth for all feature flag keys in the application.
 * Must match backend/config/feature_flags_master.yaml exactly.
 *
 * Total: 124 flags across 25 features (see FEATURE_FLAG_COUNT)
 *
 * Usage:
 *   import { FEATURE_FLAGS, FeatureFlagKey } from '@/constants/featureFlags';
 *
 *   const canViewTools = flags[FEATURE_FLAGS.TOOLS_VIEW];
 *   const canAccessMarketplace = flags[FEATURE_FLAGS.TOOLS_MARKETPLACE];
 */

import {
  getAllTreeViewFlagKeys,
  isViewFlagEnabledWithAncestors,
} from '../features/platform-admin/featureFlagsTree'

/** All `{path}_view` keys derived from the role-access tree. These don't appear in
 *  FEATURE_FLAG_META but are valid flags seeded by the backend. */
const TREE_VIEW_FLAG_KEYS: ReadonlySet<string> = new Set(getAllTreeViewFlagKeys())

// =============================================================================
// FEATURE FLAG KEYS - Matches backend exactly
// =============================================================================

export const FEATURE_FLAGS = {
  // Core (9 flags)
  HOME_VIEW: 'home_view',
  HOME_AGENTS_VIEW: 'home_agents_view',
  HOME_WORKFLOWS_VIEW: 'home_workflows_view',
  HOME_EVALS_VIEW: 'home_evals_view',
  HOME_SESSIONS_VIEW: 'home_sessions_view',
  HOME_COST_VIEW: 'home_cost_view',
  HOME_PLATFORM_VIEW: 'home_platform_view',
  SETTINGS_VIEW: 'settings_view',
  SETTINGS_EDIT: 'settings_edit',

  // Solutions (4 flags)
  SOLUTIONS_VIEW: 'solutions_view',
  SOLUTIONS_CREATE: 'solutions_create',
  SOLUTIONS_EDIT: 'solutions_edit',
  SOLUTIONS_DELETE: 'solutions_delete',

  // Projects (5 flags)
  PROJECTS_VIEW: 'projects_view',
  PROJECTS_CREATE: 'projects_create',
  PROJECTS_EDIT: 'projects_edit',
  PROJECTS_DELETE: 'projects_delete',
  PROJECTS_CONFIGURE: 'projects_configure',
  PROJECTS_DETAILS: 'projects_details',

  // Data (6 flags)
  DATA_VIEW: 'data_view',
  DATA_CREATE: 'data_create',
  DATA_EDIT: 'data_edit',
  DATA_DELETE: 'data_delete',
  DATA_UPLOAD: 'data_upload',
  DATA_SYNC: 'data_sync',

  // Prompts (6 flags)
  PROMPTS_VIEW: 'prompts_view',
  PROMPTS_CREATE: 'prompts_create',
  PROMPTS_EDIT: 'prompts_edit',
  PROMPTS_DELETE: 'prompts_delete',
  PROMPTS_PLAYGROUND: 'prompts_playground',
  PROMPTS_COMPARE_MODE: 'prompts_compare_mode',

  // Skills (5 flags)
  SKILLS_VIEW: 'skills_view',
  SKILLS_CREATE: 'skills_create',
  SKILLS_EDIT: 'skills_edit',
  SKILLS_DELETE: 'skills_delete',
  SKILLS_MANAGE_ACCESS: 'skills_manage_access',

  // Agent Studio (9 flags)
  AGENT_STUDIO_VIEW: 'agent_studio_view',
  AGENT_STUDIO_CREATE: 'agent_studio_create',
  AGENT_STUDIO_EDIT: 'agent_studio_edit',
  AGENT_STUDIO_DELETE: 'agent_studio_delete',
  AGENT_STUDIO_CANVAS: 'agent_studio_canvas',
  AGENT_STUDIO_TRACES: 'agent_studio_traces',
  AGENT_STUDIO_LOGS: 'agent_studio_logs',
  AGENT_STUDIO_CHAT: 'agent_studio_chat',
  AGENT_STUDIO_CLONE: 'agent_studio_clone',

  // Voice Agents (6 flags)
  VOICE_AGENTS_VIEW: 'voice_agents_view',
  VOICE_AGENTS_MANAGE_NUMBERS: 'voice_agents_manage_numbers_view',
  VOICE_AGENTS_MANAGE_CALLS: 'voice_agents_manage_calls_view',
  VOICE_AGENTS_MANAGE_RECORDINGS: 'voice_agents_manage_recordings_view',
  VOICE_AGENTS_ANALYTICS: 'voice_agents_analytics_view',
  VOICE_AGENTS_CANVAS: 'voice_agents_canvas_view',

  /** Agent Studio — Build with AI (list + canvas) and /agent-studio/ai-builder (ABA) — shown under Agent Studio in feature flags */
  AGENT_BUILDER_VIEW: 'agent_builder_view',
  /** Agent Workflows — Build with AI template card + /agent-workflows/ai-builder (CWB) — shown under Agent Workflows in feature flags */
  WORKFLOW_BUILDER_AI_VIEW: 'workflow_builder_ai_view',
  /** AI Blueprints (Blueprints + DIP) — nav + /blueprints & /dip — own AI Blueprints section in feature flags */
  AI_BLUEPRINTS_VIEW: 'ai_blueprints_view',
  AI_BLUEPRINTS_CREATE: 'ai_blueprints_create',
  AI_BLUEPRINTS_EDIT: 'ai_blueprints_edit',
  AI_BLUEPRINTS_DELETE: 'ai_blueprints_delete',

  // Ideate (4 flags)
  IDEATE_VIEW: 'ideate_view',
  IDEATE_CREATE: 'ideate_create',
  IDEATE_EDIT: 'ideate_edit',
  IDEATE_DELETE: 'ideate_delete',

  // Agent Workflows (5 flags)
  AGENT_WORKFLOWS_VIEW: 'agent_workflows_view',
  AGENT_WORKFLOWS_CREATE: 'agent_workflows_create',
  AGENT_WORKFLOWS_EDIT: 'agent_workflows_edit',
  AGENT_WORKFLOWS_DELETE: 'agent_workflows_delete',
  AGENT_WORKFLOWS_TENANT_METRICS: 'agent_workflows_tenant_metrics',

  // Agent Registry (4 flags)
  AGENT_REGISTRY_VIEW: 'agent_registry_view',
  AGENT_REGISTRY_CREATE: 'agent_registry_create',
  AGENT_REGISTRY_EDIT: 'agent_registry_edit',
  AGENT_REGISTRY_DELETE: 'agent_registry_delete',

  /** Workflow catalog tab under Agent Registry (tenant workflow registry) */
  WORKFLOW_REGISTRY_VIEW: 'workflow_registry_view',

  /** AI Transparency (Master/Sub-agent inventory view) — RBAC reuses agent-registry. */
  AI_TRANSPARENCY_VIEW: 'ai_transparency_view',

  // Evals and Simulations (1 flag - gate for nav and page)
  EVALS_SIMULATIONS_VIEW: 'evals_simulations_view',

  // Simulations (4 flags)
  SIMULATIONS_VIEW: 'simulations_view',
  SIMULATIONS_CREATE: 'simulations_create',
  SIMULATIONS_EDIT: 'simulations_edit',
  SIMULATIONS_DELETE: 'simulations_delete',

  // Monitoring (1 flag)
  MONITORING_VIEW: 'monitoring_view',
  OTLP_EXTERNAL_INGEST: 'otlp_external_ingest',
  AQUA_MCP_SERVER: 'aqua_mcp_server',
  AQUA_MCP_UPDATE_AGENT_GRAPH: 'aqua_mcp_update_agent_graph',

  // Cache (1 flag)
  CACHE_DASHBOARD: 'cache_dashboard',

  // Models (5 flags)
  MODELS_VIEW: 'models_view',
  MODELS_CREATE: 'models_create',
  MODELS_EDIT: 'models_edit',
  MODELS_DELETE: 'models_delete',
  MODELS_MANAGE_ACCESS: 'models_manage_access',

  // Tools (8 flags)
  TOOLS_VIEW: 'tools_view',
  TOOLS_CREATE: 'tools_create',
  TOOLS_EDIT: 'tools_edit',
  TOOLS_DELETE: 'tools_delete',
  TOOLS_MARKETPLACE: 'tools_marketplace',
  TOOLS_EXECUTION_LOGS: 'tools_execution_logs',
  TOOLS_MANAGE_ACCESS: 'tools_manage_access',
  TOOLS_TEST_MODAL: 'tools_test_modal',

  // Connections (6 flags)
  CONNECTIONS_VIEW: 'connections_view',
  CONNECTIONS_CREATE: 'connections_create',
  CONNECTIONS_EDIT: 'connections_edit',
  CONNECTIONS_DELETE: 'connections_delete',
  CONNECTIONS_MANAGE_ACCESS: 'connections_manage_access',
  CONNECTIONS_AZURE_OBO_ENABLED: 'connections_azure_obo_enabled',
  CONNECTIONS_GCP_WIF_ENABLED: 'connections_gcp_wif_enabled',

  // Storage (2 flags)
  STORAGE_MANAGE_ACCESS: 'storage_manage_access',
  STORAGE_S3_ENABLED: 'storage_s3_enabled',

  // Knowledge (10 flags) - SQL validation and connection pooling are mandatory/always-on
  KNOWLEDGE_VIEW: 'knowledge_view',
  KNOWLEDGE_CREATE: 'knowledge_create',
  KNOWLEDGE_EDIT: 'knowledge_edit',
  KNOWLEDGE_DELETE: 'knowledge_delete',
  KNOWLEDGE_SEARCH: 'knowledge_search',
  KNOWLEDGE_MANAGE_ACCESS: 'knowledge_manage_access',
  KNOWLEDGE_ERROR_CORRECTION: 'knowledge_error_correction',
  KNOWLEDGE_SCHEMA_RETRIEVAL: 'knowledge_schema_retrieval',
  KNOWLEDGE_QUERY_AUDIT: 'knowledge_query_audit',
  KNOWLEDGE_SCHEMA_EDITOR: 'knowledge_schema_editor',

  // Chat (2 flags)
  CHAT_VIEW: 'chat_view',
  CHAT_MODEL_ARENA: 'chat_model_arena',

  // Agent Store (2 flags)
  AGENT_STORE_VIEW: 'agent_store_view',
  AGENT_STORE_CHAT: 'agent_store_chat',

  // Workflow Store (2 flags)
  WORKFLOW_STORE_VIEW: 'workflow_store_view',
  WORKFLOW_STORE_EXECUTE: 'workflow_store_execute',

  // Agent Sandbox (2 flags)
  AGENT_SANDBOX_VIEW: 'agent_sandbox_view',
  AGENT_SANDBOX_CHAT: 'agent_sandbox_chat',

  // Admin (7 flags)
  ADMIN_VIEW: 'admin_view',
  ADMIN_USERS_VIEW: 'admin_users_view',
  ADMIN_USERS_APPROVE: 'admin_users_approve',
  ADMIN_USERS_REJECT: 'admin_users_reject',
  ADMIN_USERS_EDIT: 'admin_users_edit',
  ADMIN_USERS_DELETE: 'admin_users_delete',
  ADMIN_USERS_RESET_PASSWORD: 'admin_users_reset_password',

  // Admin Roles (4 flags)
  ADMIN_ROLES_VIEW: 'admin_roles_view',
  ADMIN_ROLES_CREATE: 'admin_roles_create',
  ADMIN_ROLES_EDIT: 'admin_roles_edit',
  ADMIN_ROLES_DELETE: 'admin_roles_delete',

  // Platform Admin (7 flags)
  PLATFORM_ADMIN_VIEW: 'platform_admin_view',
  PLATFORM_ADMIN_TENANTS_VIEW: 'platform_admin_tenants_view',
  PLATFORM_ADMIN_TENANTS_CREATE: 'platform_admin_tenants_create',
  PLATFORM_ADMIN_TENANTS_EDIT: 'platform_admin_tenants_edit',
  PLATFORM_ADMIN_TENANTS_DELETE: 'platform_admin_tenants_delete',
  PLATFORM_ADMIN_FEATURES_VIEW: 'platform_admin_features_view',
  PLATFORM_ADMIN_FEATURES_EDIT: 'platform_admin_features_edit',

  // Export/Import (11 flags) - view is covered by entity-specific flags, warnings always shown
  EXPORT_IMPORT_CREATE: 'export_import_create',
  EXPORT_IMPORT_MODELS: 'export_import_models',
  EXPORT_IMPORT_TOOLS: 'export_import_tools',
  EXPORT_IMPORT_KNOWLEDGE: 'export_import_knowledge',
  EXPORT_IMPORT_CONNECTORS: 'export_import_connectors',
  EXPORT_IMPORT_IAM: 'export_import_iam',
  EXPORT_IMPORT_PROMPTS: 'export_import_prompts',
  EXPORT_IMPORT_SKILLS: 'export_import_skills',
  EXPORT_IMPORT_AGENTS: 'export_import_agents',
  EXPORT_IMPORT_BLUEPRINTS: 'export_import_blueprints',
  EXPORT_IMPORT_BUNDLES: 'export_import_bundles',

  // Memory System (15 flags) - token tracking is always-on for cost monitoring
  MEMORY_SYSTEM_ENABLED: 'memory_system_enabled',
  MEMORY_WORKING_ENABLED: 'memory_working_enabled',
  MEMORY_EPISODIC_ENABLED: 'memory_episodic_enabled',
  MEMORY_SEMANTIC_ENABLED: 'memory_semantic_enabled',
  MEMORY_PROCEDURAL_ENABLED: 'memory_procedural_enabled',
  MEMORY_KNOWLEDGE_GRAPH_ENABLED: 'memory_knowledge_graph_enabled',
  MEMORY_CONSOLIDATION_ENABLED: 'memory_consolidation_enabled',
  MEMORY_CROSS_SESSION_ENABLED: 'memory_cross_session_enabled',
  MEMORY_SETTINGS_VIEW: 'memory_settings_view',
  MEMORY_SETTINGS_EDIT: 'memory_settings_edit',
  MEMORY_DEBUG_VIEW: 'memory_debug_view',
  MEMORY_USER_CONTROLS: 'memory_user_controls',
  MEMORY_FEW_SHOT_ENABLED: 'memory_few_shot_enabled',
  MEMORY_TOOL_MEMORY_ENABLED: 'memory_tool_memory_enabled',
  MEMORY_SELF_REFLECTION_ENABLED: 'memory_self_reflection_enabled',
  MEMORY_PRIVACY_CONTROLS_ENABLED: 'memory_privacy_controls_enabled',

  // RAG Advanced Features (11 flags)
  RAG_ENHANCEMENTS_DISABLE_OPTION: 'rag_enhancements_disable_option',
  RAG_HYDE_ENABLED: 'rag_hyde_enabled',
  RAG_MULTI_QUERY_ENABLED: 'rag_multi_query_enabled',
  RAG_DECOMPOSITION_ENABLED: 'rag_decomposition_enabled',
  RAG_ADAPTIVE_ENABLED: 'rag_adaptive_enabled',
  RAG_SELF_RAG_ENABLED: 'rag_self_rag_enabled',
  RAG_CITATIONS_ENABLED: 'rag_citations_enabled',
  RAG_CITATION_VERIFICATION: 'rag_citation_verification',
  RAG_ANALYTICS_ENABLED: 'rag_analytics_enabled',
  RAG_PLAYGROUND_ENABLED: 'rag_playground_enabled',
  RAG_API_ENABLED: 'rag_api_enabled',

  // Reflection & Self-Correction (8 flags)
  REFLECTION_ENGINE_ENABLED: 'reflection_engine_enabled',
  REFLECTION_UI_VISIBLE: 'reflection_ui_visible',
  REFLECTION_CRITIC_ENABLED: 'reflection_critic_enabled',
  REFLECTION_RISE_ENABLED: 'reflection_rise_enabled',
  REFLECTION_MAR_ENABLED: 'reflection_mar_enabled',
  REFLECTION_LATS_ENABLED: 'reflection_lats_enabled',
  REFLECTION_META_JUDGE_ENABLED: 'reflection_meta_judge_enabled',
  REFLECTION_RL_COLLECTION_ENABLED: 'reflection_rl_collection_enabled',

  // Multi-Modal Features (10 flags)
  MULTIMODAL_VISION_ENABLED: 'multimodal_vision_enabled',
  MULTIMODAL_VISION_OCR_ENABLED: 'multimodal_vision_ocr_enabled',
  MULTIMODAL_VISION_CHART_ANALYSIS: 'multimodal_vision_chart_analysis',
  MULTIMODAL_AUDIO_ENABLED: 'multimodal_audio_enabled',
  MULTIMODAL_AUDIO_TTS_ENABLED: 'multimodal_audio_tts_enabled',
  MULTIMODAL_DOCUMENT_ENABLED: 'multimodal_document_enabled',
  MULTIMODAL_DOCUMENT_TABLES: 'multimodal_document_tables',
  MULTIMODAL_API_ENABLED: 'multimodal_api_enabled',
  MULTIMODAL_INPUT: 'multimodal_input',

  // Document Knowledge Bank (7 flags) - processing tied to master switch
  DOCUMENT_KNOWLEDGE_BANK_ENABLED: 'document_knowledge_bank_enabled',
  DOCUMENT_UPLOAD_ENABLED: 'document_upload_enabled',
  DOCUMENT_DEDUPLICATION_ENABLED: 'document_deduplication_enabled',
  DOCUMENT_AUTO_ARCHIVE_ENABLED: 'document_auto_archive_enabled',
  DOCUMENT_VECTOR_SEARCH_ENABLED: 'document_vector_search_enabled',
  DOCUMENT_RERANKING_ENABLED: 'document_reranking_enabled',
  DOCUMENT_CONFIG_UI_ENABLED: 'document_config_ui_enabled',

  // Agent Evals (10 flags) - evaluation sessions and templates
  AGENT_EVALS_VIEW: 'agent_evals_view',
  AGENT_EVALS_CREATE: 'agent_evals_create',
  AGENT_EVALS_EDIT: 'agent_evals_edit',
  AGENT_EVALS_DELETE: 'agent_evals_delete',
  AGENT_EVALS_TEMPLATES_VIEW: 'agent_evals_templates_view',
  AGENT_EVALS_TEMPLATES_CREATE: 'agent_evals_templates_create',
  AGENT_EVALS_TEMPLATES_EDIT: 'agent_evals_templates_edit',
  AGENT_EVALS_TEMPLATES_DELETE: 'agent_evals_templates_delete',
  AGENT_EVALS_CUSTOM_JUDGE: 'agent_evals_custom_judge',
  AGENT_EVALS_EXPORT: 'agent_evals_export',

  // Enterprise Ontology (13 flags)
  ONTOLOGY_CANVAS_VIEW: 'ontology_canvas_view',
  ONTOLOGY_CANVAS_EDIT: 'ontology_canvas_edit',
  ONTOLOGY_BUILD_WITH_AI_VIEW: 'ontology_build_with_ai_view',
  ONTOLOGY_BUILD_WITH_AI_CREATE: 'ontology_build_with_ai_create',
  ONTOLOGY_BUILD_WITH_AI_EDIT: 'ontology_build_with_ai_edit',
  ONTOLOGY_BUILD_WITH_AI_DELETE: 'ontology_build_with_ai_delete',
  ONTOLOGY_DATA_MODELS_VIEW: 'ontology_data_models_view',
  ONTOLOGY_DATA_MODELS_EDIT: 'ontology_data_models_edit',
  ONTOLOGY_KPI_LIBRARY_VIEW: 'ontology_kpi_library_view',
  ONTOLOGY_KPI_LIBRARY_EDIT: 'ontology_kpi_library_edit',
  ONTOLOGY_PIPELINES_VIEW: 'ontology_pipelines_view',
  ONTOLOGY_PIPELINE_DESIGNER_VIEW: 'ontology_pipeline_designer_view',
  ONTOLOGY_OBSERVABILITY_VIEW: 'ontology_observability_view',
  ONTOLOGY_MIGRATIONS_VIEW: 'ontology_migrations_view',
  ONTOLOGY_MIGRATIONS_EDIT: 'ontology_migrations_edit',
  ONTOLOGY_MIGRATIONS_EXECUTE: 'ontology_migrations_execute',

  // Docs (4 flags)
  DOCS_VIEW: 'docs_view',
  DOCS_CREATE: 'docs_create',
  DOCS_EDIT: 'docs_edit',
  DOCS_DELETE: 'docs_delete',

  // SOTA Supervisor Architecture (6 flags)
  SUPERVISOR_SUBGRAPH_EXECUTION: 'supervisor_subgraph_execution',
  SUPERVISOR_TASK_DECOMPOSITION: 'supervisor_task_decomposition',
  SUPERVISOR_CONTEXT_DISTILLATION: 'supervisor_context_distillation',
  SUPERVISOR_ITERATIVE_REFINEMENT: 'supervisor_iterative_refinement',
  SUPERVISOR_COMPLETENESS_EVAL: 'supervisor_completeness_eval',
  SUPERVISOR_SOTA_UI: 'supervisor_sota_ui',
} as const

// TypeScript type for all valid flag keys
export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS]

// Type for the feature flags object (key -> boolean)
export type FeatureFlags = Record<FeatureFlagKey, boolean>

// =============================================================================
// FEATURE FLAG CATEGORIES
// =============================================================================

export type FeatureFlagCategory =
  | 'core'
  | 'agent_development'
  | 'ai_blueprints'
  | 'observability'
  | 'agent_ops'
  | 'interaction'
  | 'administration'
  | 'platform_admin'

export const FEATURE_FLAG_CATEGORIES: Record<FeatureFlagCategory, string> = {
  core: 'Core',
  agent_development: 'Agent Development',
  ai_blueprints: 'Blueprints',
  observability: 'Observability',
  agent_ops: 'Agent Ops',
  interaction: 'Interaction',
  administration: 'Administration',
  platform_admin: 'Platform Admin',
}

/** Block title in Platform Admin / tenant feature UI (overrides naive title case). */
export function getFeatureBlockDisplayName(featureId: string): string {
  const overrides: Record<string, string> = {
    'ai-blueprints': 'Blueprints',
    'agent-workflows': 'Agent Workflows',
  }
  if (overrides[featureId]) return overrides[featureId]
  return featureId
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
}

// =============================================================================
// FEATURE FLAG METADATA
// =============================================================================

export interface FeatureFlagMeta {
  key: FeatureFlagKey
  displayName: string
  description: string
  category: FeatureFlagCategory
  feature: string // Parent feature ID
  /** When true, `createDefaultFlags` initializes this flag to true. Use for
   *  client-only features that aren't (yet) in backend feature_flags_master.yaml
   *  so they don't appear disabled at tenant level by default. */
  defaultEnabled?: boolean
}

/**
 * Metadata for all feature flags.
 * Used for rendering in the Platform Admin UI.
 */
export const FEATURE_FLAG_META: FeatureFlagMeta[] = [
  // Core
  {
    key: 'home_view',
    displayName: 'Home View',
    description: 'View the home dashboard',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_agents_view',
    displayName: 'Home Agents Tab',
    description: 'View agents dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_workflows_view',
    displayName: 'Home Workflows Tab',
    description: 'View workflows dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_evals_view',
    displayName: 'Home Evals Tab',
    description: 'View evals dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_sessions_view',
    displayName: 'Home Sessions Tab',
    description: 'View sessions dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_cost_view',
    displayName: 'Home Cost Tab',
    description: 'View cost & token analytics dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'home_platform_view',
    displayName: 'Home Platform Tab',
    description: 'View platform admin dashboard tab',
    category: 'core',
    feature: 'home',
  },
  {
    key: 'settings_view',
    displayName: 'Settings View',
    description: 'View user settings',
    category: 'core',
    feature: 'settings',
  },
  {
    key: 'settings_edit',
    displayName: 'Settings Edit',
    description: 'Edit user settings',
    category: 'core',
    feature: 'settings',
  },
  {
    key: 'solutions_view',
    displayName: 'Solutions View',
    description: 'View solutions catalog and open solution editor',
    category: 'core',
    feature: 'solutions',
  },
  {
    key: 'solutions_create',
    displayName: 'Solutions Create',
    description: 'Create solutions and upload code archives',
    category: 'core',
    feature: 'solutions',
  },
  {
    key: 'solutions_edit',
    displayName: 'Solutions Edit',
    description: 'Edit solution metadata and files',
    category: 'core',
    feature: 'solutions',
  },
  {
    key: 'solutions_delete',
    displayName: 'Solutions Delete',
    description: 'Delete solutions',
    category: 'core',
    feature: 'solutions',
  },

  // Projects
  {
    key: 'projects_view',
    displayName: 'Projects View',
    description: 'View projects list',
    category: 'agent_development',
    feature: 'projects',
  },
  {
    key: 'projects_create',
    displayName: 'Projects Create',
    description: 'Create new projects',
    category: 'agent_development',
    feature: 'projects',
  },
  {
    key: 'projects_edit',
    displayName: 'Projects Edit',
    description: 'Edit project details',
    category: 'agent_development',
    feature: 'projects',
  },
  {
    key: 'projects_delete',
    displayName: 'Projects Delete',
    description: 'Delete projects',
    category: 'agent_development',
    feature: 'projects',
  },
  {
    key: 'projects_configure',
    displayName: 'Projects Configure',
    description: 'Configure project settings',
    category: 'agent_development',
    feature: 'projects',
  },
  {
    key: 'projects_details',
    displayName: 'Projects Details',
    description: 'View project details hub (mini Aqua tabs)',
    category: 'agent_development',
    feature: 'projects',
    defaultEnabled: true,
  },

  // Data
  {
    key: 'data_view',
    displayName: 'Data View',
    description: 'View data sources',
    category: 'agent_development',
    feature: 'data',
  },
  {
    key: 'data_create',
    displayName: 'Data Create',
    description: 'Create data sources',
    category: 'agent_development',
    feature: 'data',
  },
  {
    key: 'data_edit',
    displayName: 'Data Edit',
    description: 'Edit data sources',
    category: 'agent_development',
    feature: 'data',
  },
  {
    key: 'data_delete',
    displayName: 'Data Delete',
    description: 'Delete data sources',
    category: 'agent_development',
    feature: 'data',
  },
  {
    key: 'data_upload',
    displayName: 'Data Upload',
    description: 'Upload data files',
    category: 'agent_development',
    feature: 'data',
  },
  {
    key: 'data_sync',
    displayName: 'Data Sync',
    description: 'Sync/refresh metadata for registered tables',
    category: 'agent_development',
    feature: 'data',
  },

  // Prompts
  {
    key: 'prompts_view',
    displayName: 'Prompts View',
    description: 'View prompts',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'prompts_create',
    displayName: 'Prompts Create',
    description: 'Create prompts',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'prompts_edit',
    displayName: 'Prompts Edit',
    description: 'Edit prompts',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'prompts_delete',
    displayName: 'Prompts Delete',
    description: 'Delete prompts',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'prompts_playground',
    displayName: 'Prompts Playground',
    description: 'Use prompt playground',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'prompts_compare_mode',
    displayName: 'Prompts Compare Mode',
    description: 'Enable prompt diff/comparison feature',
    category: 'agent_development',
    feature: 'prompts',
  },

  // Skills
  {
    key: 'skills_view',
    displayName: 'Skills View',
    description: 'View skills',
    category: 'agent_development',
    feature: 'skills',
  },
  {
    key: 'skills_create',
    displayName: 'Skills Create',
    description: 'Create skills',
    category: 'agent_development',
    feature: 'skills',
  },
  {
    key: 'skills_edit',
    displayName: 'Skills Edit',
    description: 'Edit skills',
    category: 'agent_development',
    feature: 'skills',
  },
  {
    key: 'skills_delete',
    displayName: 'Skills Delete',
    description: 'Delete skills',
    category: 'agent_development',
    feature: 'skills',
  },
  {
    key: 'skills_manage_access',
    displayName: 'Skills Manage Access',
    description: 'Manage skill access permissions',
    category: 'agent_development',
    feature: 'skills',
  },

  // Agent Studio
  {
    key: 'agent_studio_view',
    displayName: 'Agent Studio View',
    description: 'View Agent Studio',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_create',
    displayName: 'Agent Studio Create',
    description: 'Create agents',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_edit',
    displayName: 'Agent Studio Edit',
    description: 'Edit agents',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_delete',
    displayName: 'Agent Studio Delete',
    description: 'Delete agents',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_canvas',
    displayName: 'Agent Studio Canvas',
    description: 'Use visual canvas',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_traces',
    displayName: 'Agent Studio Traces',
    description: 'View execution traces',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_logs',
    displayName: 'Agent Studio Logs',
    description: 'View agent logs',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_chat',
    displayName: 'Agent Studio Chat',
    description: 'Chat with agents',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'agent_studio_clone',
    displayName: 'Agent Studio Clone',
    description: 'Clone existing agents into draft mode',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  // Voice Agents
  {
    key: 'voice_agents_view',
    displayName: 'Voice Agents View',
    description: 'View voice agent capabilities and voice node palette',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'voice_agents_manage_numbers',
    displayName: 'Voice Agents Manage Numbers',
    description: 'Manage phone numbers for voice agents',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'voice_agents_manage_calls',
    displayName: 'Voice Agents Manage Calls',
    description: 'View and manage voice call history',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'voice_agents_manage_recordings',
    displayName: 'Voice Agents Manage Recordings',
    description: 'View and manage call recordings',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'voice_agents_analytics',
    displayName: 'Voice Agents Analytics',
    description: 'View voice call analytics and metrics',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'voice_agents_canvas_view',
    displayName: 'Voice Agents Canvas',
    description: 'Use voice dialog nodes in Agent Studio canvas',
    category: 'agent_development',
    feature: 'voice-agents',
  },
  {
    key: 'agent_builder_view',
    displayName: 'Agent Builder',
    description:
      'Agent Studio Build with AI entry points and AI Agent Builder (ABA) route',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'workflow_builder_ai_view',
    displayName: 'Workflow Builder',
    description:
      'Agent Workflows Build with AI template option and AI Workflow Builder (CWB) route',
    category: 'agent_development',
    feature: 'agent-workflows',
  },
  {
    key: 'ai_blueprints_view',
    displayName: 'Blueprints',
    description: 'Blueprints library and Decision Intelligence Platform landing (nav and /blueprints, /dip)',
    category: 'ai_blueprints',
    feature: 'ai-blueprints',
  },

  // Ideate (client-only — no backend feature flag yet, so default to enabled)
  {
    key: 'ideate_view',
    displayName: 'Ideate View',
    description: 'View the Ideate page and blueprint listings',
    category: 'ai_blueprints',
    feature: 'ideate',
    defaultEnabled: true,
  },
  {
    key: 'ideate_create',
    displayName: 'Ideate Create',
    description: 'Create new Ideate blueprints',
    category: 'ai_blueprints',
    feature: 'ideate',
    defaultEnabled: true,
  },
  {
    key: 'ideate_edit',
    displayName: 'Ideate Edit',
    description: 'Edit Ideate blueprints',
    category: 'ai_blueprints',
    feature: 'ideate',
    defaultEnabled: true,
  },
  {
    key: 'ideate_delete',
    displayName: 'Ideate Delete',
    description: 'Delete Ideate blueprints',
    category: 'ai_blueprints',
    feature: 'ideate',
    defaultEnabled: true,
  },

  // Docs
  {
    key: 'docs_view',
    displayName: 'Docs View',
    description: 'View product documentation',
    category: 'core',
    feature: 'docs',
    defaultEnabled: true,
  },
  {
    key: 'docs_create',
    displayName: 'Docs Create',
    description: 'Create documentation entries',
    category: 'core',
    feature: 'docs',
    defaultEnabled: true,
  },
  {
    key: 'docs_edit',
    displayName: 'Docs Edit',
    description: 'Edit documentation entries',
    category: 'core',
    feature: 'docs',
    defaultEnabled: true,
  },
  {
    key: 'docs_delete',
    displayName: 'Docs Delete',
    description: 'Delete documentation entries',
    category: 'core',
    feature: 'docs',
    defaultEnabled: true,
  },

  // Agent Workflows
  {
    key: 'agent_workflows_view',
    displayName: 'Agent Workflows View',
    description: 'View workflows',
    category: 'agent_development',
    feature: 'agent-workflows',
  },
  {
    key: 'agent_workflows_create',
    displayName: 'Agent Workflows Create',
    description: 'Create workflows',
    category: 'agent_development',
    feature: 'agent-workflows',
  },
  {
    key: 'agent_workflows_edit',
    displayName: 'Agent Workflows Edit',
    description: 'Edit workflows',
    category: 'agent_development',
    feature: 'agent-workflows',
  },
  {
    key: 'agent_workflows_delete',
    displayName: 'Agent Workflows Delete',
    description: 'Delete workflows',
    category: 'agent_development',
    feature: 'agent-workflows',
  },
  {
    key: 'agent_workflows_tenant_metrics',
    displayName: 'Agent Workflows Tenant Metrics',
    description: 'View workflow metrics at tenant level (all accessible projects)',
    category: 'agent_development',
    feature: 'agent-workflows',
  },

  // Enterprise Ontology
  { key: 'ontology_canvas_view', displayName: 'Ontology Canvas View', description: 'View the Enterprise Ontology Canvas', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_canvas_edit', displayName: 'Ontology Canvas Edit', description: 'Edit ontology graph (schema editor)', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_data_models_view', displayName: 'Data Models View', description: 'View enterprise data model catalog', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_data_models_edit', displayName: 'Data Models Edit', description: 'Edit data model definitions', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_kpi_library_view', displayName: 'KPI Library View', description: 'View KPI definitions catalog', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_kpi_library_edit', displayName: 'KPI Library Edit', description: 'Edit KPI definitions', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_pipelines_view', displayName: 'Data Studio View', description: 'View data pipeline catalog and details', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_pipeline_designer_view', displayName: 'ML Studio View', description: 'View and manage orchestrator workflow designs', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_observability_view', displayName: 'Data Observability View', description: 'View data observability dashboard', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_migrations_view', displayName: 'Migrations Studio View', description: 'View AI-powered Migrations Studio', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_migrations_edit', displayName: 'Migrations Studio Edit', description: 'Create and edit migration projects', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_migrations_execute', displayName: 'Migrations Studio Execute', description: 'Execute migration pipelines', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_build_with_ai_view', displayName: 'Build Ontology with AI View', description: 'Build OWL ontologies with AI from a data model', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_build_with_ai_create', displayName: 'Build Ontology with AI Create', description: 'Create Ontology Builder sessions and register results', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_build_with_ai_edit', displayName: 'Build Ontology with AI Edit', description: 'Edit Ontology Builder sessions and competency questions', category: 'agent_development', feature: 'ontology' },
  { key: 'ontology_build_with_ai_delete', displayName: 'Build Ontology with AI Delete', description: 'Delete Ontology Builder sessions', category: 'agent_development', feature: 'ontology' },

  // Supervisor Architecture
  {
    key: 'supervisor_subgraph_execution',
    displayName: 'Supervisor Subgraph Execution',
    description: 'Enable delegates as full sub-graphs',
    category: 'agent_development',
    feature: 'supervisor',
  },
  {
    key: 'supervisor_task_decomposition',
    displayName: 'Supervisor Task Decomposition',
    description: 'Enable task decomposition for delegates',
    category: 'agent_development',
    feature: 'supervisor',
  },
  {
    key: 'supervisor_context_distillation',
    displayName: 'Supervisor Context Distillation',
    description: 'Distill raw delegate outputs into concise insights',
    category: 'agent_development',
    feature: 'supervisor',
  },
  {
    key: 'supervisor_iterative_refinement',
    displayName: 'Supervisor Iterative Refinement',
    description: 'Enable iterative research loop',
    category: 'agent_development',
    feature: 'supervisor',
  },
  {
    key: 'supervisor_completeness_eval',
    displayName: 'Supervisor Completeness Eval',
    description: 'Enable completeness evaluation between iterations',
    category: 'agent_development',
    feature: 'supervisor',
  },
  {
    key: 'supervisor_sota_ui',
    displayName: 'Supervisor Advanced UI',
    description: 'Show Advanced configuration options in UI',
    category: 'agent_development',
    feature: 'supervisor',
  },

  // Agent Registry
  {
    key: 'agent_registry_view',
    displayName: 'Agent Registry View',
    description: 'View agent registry',
    category: 'observability',
    feature: 'agent-registry',
  },
  {
    key: 'agent_registry_create',
    displayName: 'Agent Registry Create',
    description: 'Register agents',
    category: 'observability',
    feature: 'agent-registry',
  },
  {
    key: 'agent_registry_edit',
    displayName: 'Agent Registry Edit',
    description: 'Edit registered agents',
    category: 'observability',
    feature: 'agent-registry',
  },
  {
    key: 'agent_registry_delete',
    displayName: 'Agent Registry Delete',
    description: 'Remove from registry',
    category: 'observability',
    feature: 'agent-registry',
  },
  {
    key: 'workflow_registry_view',
    displayName: 'Workflow Registry View',
    description: 'View workflow registry (Agent Registry Workflows tab)',
    category: 'observability',
    feature: 'agent-registry',
  },
  {
    key: 'ai_transparency_view',
    displayName: 'AI Transparency View',
    description: 'View AI Transparency (Master/Sub-agent inventory)',
    category: 'observability',
    feature: 'agent-registry',
  },

  // Evals and Simulations
  {
    key: 'evals_simulations_view',
    displayName: 'Evals and Simulations View',
    description: 'View Evals and Simulations section (Leaderboards, Benchmarks, Environments, Evaluations, Simulations)',
    category: 'observability',
    feature: 'evals-simulations',
  },

  // Simulations (tenant RL/simulator flags — grouped under Evals and Simulations in Role UI)
  {
    key: 'simulations_view',
    displayName: 'Simulations View',
    description: 'View simulations',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'simulations_create',
    displayName: 'Simulations Create',
    description: 'Create simulations',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'simulations_edit',
    displayName: 'Simulations Edit',
    description: 'Edit simulations',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'simulations_delete',
    displayName: 'Simulations Delete',
    description: 'Delete simulations',
    category: 'observability',
    feature: 'evals-simulations',
  },

  // Monitoring
  {
    key: 'monitoring_view',
    displayName: 'Monitoring View',
    description: 'View monitoring dashboard',
    category: 'observability',
    feature: 'monitoring',
  },
  // Cache (sub-feature of Monitoring)
  {
    key: 'cache_dashboard',
    displayName: 'Cache Dashboard',
    description: 'View intelligent cache metrics, health, and configuration',
    category: 'observability',
    feature: 'monitoring',
  },
  {
    key: 'otlp_external_ingest',
    displayName: 'OTLP External Trace Ingest',
    description:
      'Allow this tenant to mint telemetry ingest keys and push OTLP traces through the platform collector',
    category: 'observability',
    feature: 'monitoring',
  },
  {
    key: 'aqua_mcp_server',
    displayName: 'MCP Server (Cowork)',
    description:
      'Expose Agent Studio and Agent Workflows as an MCP server so Claude Cowork and other MCP hosts can author assets with the same RBAC as the UI',
    category: 'agent_ops',
    feature: 'agent-studio',
  },
  {
    key: 'aqua_mcp_update_agent_graph',
    displayName: 'MCP Update Agent Graph',
    description:
      'Allow MCP hosts to replace an agent graph_definition in one call (escape hatch; keep off unless debugging)',
    category: 'agent_ops',
    feature: 'agent-studio',
    defaultEnabled: false,
  },

  // Models
  {
    key: 'models_view',
    displayName: 'Models View',
    description: 'View models',
    category: 'agent_ops',
    feature: 'models',
  },
  {
    key: 'models_create',
    displayName: 'Models Create',
    description: 'Add models',
    category: 'agent_ops',
    feature: 'models',
  },
  {
    key: 'models_edit',
    displayName: 'Models Edit',
    description: 'Edit models',
    category: 'agent_ops',
    feature: 'models',
  },
  {
    key: 'models_delete',
    displayName: 'Models Delete',
    description: 'Delete models',
    category: 'agent_ops',
    feature: 'models',
  },
  {
    key: 'models_manage_access',
    displayName: 'Models Manage Access',
    description: 'Manage model access',
    category: 'agent_ops',
    feature: 'models',
  },

  // Tools
  {
    key: 'tools_view',
    displayName: 'Tools View',
    description: 'View tools',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_create',
    displayName: 'Tools Create',
    description: 'Create tools',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_edit',
    displayName: 'Tools Edit',
    description: 'Edit tools',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_delete',
    displayName: 'Tools Delete',
    description: 'Delete tools',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_marketplace',
    displayName: 'Tools Marketplace',
    description: 'Access tool marketplace',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_execution_logs',
    displayName: 'Tools Execution Logs',
    description: 'View tool logs',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_manage_access',
    displayName: 'Tools Manage Access',
    description: 'Manage tool access',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'tools_test_modal',
    displayName: 'Tools Test Modal',
    description: 'Enable tool testing functionality in tool editor',
    category: 'agent_ops',
    feature: 'tools',
  },

  // Connections
  {
    key: 'connections_view',
    displayName: 'Connections View',
    description: 'View connections',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_create',
    displayName: 'Connections Create',
    description: 'Create connections',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_edit',
    displayName: 'Connections Edit',
    description: 'Edit connections',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_delete',
    displayName: 'Connections Delete',
    description: 'Delete connections',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_manage_access',
    displayName: 'Connections Manage Access',
    description: 'Manage connection access',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_azure_obo_enabled',
    displayName: 'Azure OBO Auth',
    description: 'Enable Azure On-Behalf-Of identity propagation for database connectors',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'connections_gcp_wif_enabled',
    displayName: 'GCP WIF Auth',
    description: 'Enable GCP Workload Identity Federation (Azure AD → GCP) for database connectors',
    category: 'agent_ops',
    feature: 'connections',
  },

  // Storage
  {
    key: 'storage_manage_access',
    displayName: 'Storage Manage Access',
    description: 'Manage storage access permissions',
    category: 'agent_development',
    feature: 'storage',
  },
  {
    key: 'storage_s3_enabled',
    displayName: 'Storage S3 Enabled',
    description: 'Enable AWS S3 storage connector option',
    category: 'agent_development',
    feature: 'storage',
  },

  // Knowledge
  {
    key: 'knowledge_view',
    displayName: 'Knowledge View',
    description: 'View knowledge sources',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_create',
    displayName: 'Knowledge Create',
    description: 'Create knowledge sources',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_edit',
    displayName: 'Knowledge Edit',
    description: 'Edit knowledge sources',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_delete',
    displayName: 'Knowledge Delete',
    description: 'Delete knowledge sources',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_search',
    displayName: 'Knowledge Search',
    description: 'Search knowledge sources',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_manage_access',
    displayName: 'Knowledge Manage Access',
    description: 'Manage knowledge access permissions',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_error_correction',
    displayName: 'Knowledge Error Correction',
    description: 'Enable automatic SQL error correction with retries',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_schema_retrieval',
    displayName: 'Knowledge Schema Retrieval',
    description: 'Enable semantic schema retrieval for large table sets',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_query_audit',
    displayName: 'Knowledge Query Audit',
    description: 'Enable SQL query audit trail logging',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'knowledge_schema_editor',
    displayName: 'Knowledge Schema Editor',
    description: 'Enable table ontology/schema editor page',
    category: 'agent_development',
    feature: 'knowledge',
  },

  // Memory System
  {
    key: 'memory_system_enabled',
    displayName: 'Memory System Enabled',
    description: 'Master switch for entire memory system',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_working_enabled',
    displayName: 'Memory Working Enabled',
    description: 'Enable working memory (short-term, Redis)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_episodic_enabled',
    displayName: 'Memory Episodic Enabled',
    description: 'Enable episodic memory (session-based, pgvector)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_semantic_enabled',
    displayName: 'Memory Semantic Enabled',
    description: 'Enable semantic memory (facts, entities)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_procedural_enabled',
    displayName: 'Memory Procedural Enabled',
    description: 'Enable procedural memory (skills, patterns)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_knowledge_graph_enabled',
    displayName: 'Memory Knowledge Graph Enabled',
    description: 'Enable knowledge graph (entities, relationships)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_consolidation_enabled',
    displayName: 'Memory Consolidation Enabled',
    description: 'Enable memory consolidation (episodic to semantic)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_cross_session_enabled',
    displayName: 'Memory Cross Session Enabled',
    description: 'Enable cross-session memory retrieval',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_settings_view',
    displayName: 'Memory Settings View',
    description: 'View memory settings for agents',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_settings_edit',
    displayName: 'Memory Settings Edit',
    description: 'Edit memory settings for agents',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_debug_view',
    displayName: 'Memory Debug View',
    description: 'View memory debug panel in chat',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_user_controls',
    displayName: 'Memory User Controls',
    description: 'Enable user memory controls (forget/remember)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_few_shot_enabled',
    displayName: 'Memory Few Shot Enabled',
    description: 'Enable few-shot learning from memory',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_tool_memory_enabled',
    displayName: 'Memory Tool Memory Enabled',
    description: 'Enable tool memory (SOTA procedural memory for tool calls)',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_self_reflection_enabled',
    displayName: 'Memory Self Reflection Enabled',
    description: 'Enable RLM self-reflection',
    category: 'agent_development',
    feature: 'memory',
  },
  {
    key: 'memory_privacy_controls_enabled',
    displayName: 'Memory Privacy Controls Enabled',
    description: 'Enable GDPR/privacy controls for memory',
    category: 'agent_development',
    feature: 'memory',
  },

  // RAG Advanced Features
  {
    key: 'rag_enhancements_disable_option',
    displayName: 'RAG Enhancements Disable Option',
    description: 'Enable UI option to disable all RAG enhancements',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_hyde_enabled',
    displayName: 'RAG HyDE Enabled',
    description: 'Enable HyDE (Hypothetical Document Embeddings)',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_multi_query_enabled',
    displayName: 'RAG Multi Query Enabled',
    description: 'Enable multi-query expansion',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_decomposition_enabled',
    displayName: 'RAG Decomposition Enabled',
    description: 'Enable query decomposition for complex queries',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_adaptive_enabled',
    displayName: 'RAG Adaptive Enabled',
    description: 'Enable adaptive strategy selection',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_self_rag_enabled',
    displayName: 'RAG Self RAG Enabled',
    description: 'Enable Self-RAG correction loop (CRAG pattern)',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_citations_enabled',
    displayName: 'RAG Citations Enabled',
    description: 'Enable inline citations in responses',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_citation_verification',
    displayName: 'RAG Citation Verification',
    description: 'Enable citation verification against sources',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_analytics_enabled',
    displayName: 'RAG Analytics Enabled',
    description: 'Enable RAG analytics tracking',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_playground_enabled',
    displayName: 'RAG Playground Enabled',
    description: 'Enable RAG Playground for testing',
    category: 'agent_development',
    feature: 'rag',
  },
  {
    key: 'rag_api_enabled',
    displayName: 'RAG API Enabled',
    description: 'Enable dedicated /v1/rag/* API endpoints',
    category: 'agent_development',
    feature: 'rag',
  },

  // Reflection & Self-Correction
  {
    key: 'reflection_engine_enabled',
    displayName: 'Reflection Engine Enabled',
    description: 'Master switch for reflection system',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_ui_visible',
    displayName: 'Reflection UI Visible',
    description: 'Show reflection process in chat UI',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_critic_enabled',
    displayName: 'Reflection CRITIC Enabled',
    description: 'Enable CRITIC tool-interactive verification',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_rise_enabled',
    displayName: 'Reflection RISE Enabled',
    description: 'Enable RISE recursive introspection',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_mar_enabled',
    displayName: 'Reflection MAR Enabled',
    description: 'Enable Multi-Agent Reflexion (higher cost)',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_lats_enabled',
    displayName: 'Reflection LATS Enabled',
    description: 'Enable LATS tree search (compute intensive)',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_meta_judge_enabled',
    displayName: 'Reflection Meta Judge Enabled',
    description: 'Enable Meta-Rewarding self-improving judge',
    category: 'agent_development',
    feature: 'reflection',
  },
  {
    key: 'reflection_rl_collection_enabled',
    displayName: 'Reflection RL Collection Enabled',
    description: 'Collect trajectories for Agent Lightning RL',
    category: 'agent_development',
    feature: 'reflection',
  },

  // Multi-Modal Features
  {
    key: 'multimodal_vision_enabled',
    displayName: 'Multimodal Vision Enabled',
    description: 'Enable vision/image analysis capabilities',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_vision_ocr_enabled',
    displayName: 'Multimodal Vision OCR Enabled',
    description: 'Enable OCR text extraction from images',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_vision_chart_analysis',
    displayName: 'Multimodal Vision Chart Analysis',
    description: 'Enable chart/graph interpretation',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_audio_enabled',
    displayName: 'Multimodal Audio Enabled',
    description: 'Enable audio transcription and TTS',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_audio_tts_enabled',
    displayName: 'Multimodal Audio TTS Enabled',
    description: 'Enable text-to-speech generation',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_document_enabled',
    displayName: 'Multimodal Document Enabled',
    description: 'Enable document processing (PDF, DOCX, etc.)',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_document_tables',
    displayName: 'Multimodal Document Tables',
    description: 'Enable table extraction from documents',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_api_enabled',
    displayName: 'Multimodal API Enabled',
    description: 'Enable dedicated /v1/multimodal/* API endpoints',
    category: 'agent_development',
    feature: 'multimodal',
  },
  {
    key: 'multimodal_input',
    displayName: 'Multimodal Input',
    description: 'Enable file attachment button in chat interface',
    category: 'agent_development',
    feature: 'multimodal',
  },

  // Document Knowledge Bank
  {
    key: 'document_knowledge_bank_enabled',
    displayName: 'Document Knowledge Bank Enabled',
    description: 'Master switch for Document Knowledge Bank',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_upload_enabled',
    displayName: 'Document Upload Enabled',
    description: 'Enable document uploads in chat',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_deduplication_enabled',
    displayName: 'Document Deduplication Enabled',
    description: 'Enable SHA-256 content deduplication',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_auto_archive_enabled',
    displayName: 'Document Auto Archive Enabled',
    description: 'Enable auto-archiving of old documents',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_vector_search_enabled',
    displayName: 'Document Vector Search Enabled',
    description: 'Enable vector similarity search',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_reranking_enabled',
    displayName: 'Document Reranking Enabled',
    description: 'Enable cross-encoder reranking',
    category: 'agent_development',
    feature: 'document-knowledge',
  },
  {
    key: 'document_config_ui_enabled',
    displayName: 'Document Config UI Enabled',
    description: 'Enable agent-level document settings UI',
    category: 'agent_development',
    feature: 'document-knowledge',
  },

  // Chat
  {
    key: 'chat_view',
    displayName: 'Chat View',
    description: 'Access chat interface',
    category: 'interaction',
    feature: 'chat',
  },
  {
    key: 'chat_model_arena',
    displayName: 'Chat Model Arena',
    description: 'Use model arena',
    category: 'interaction',
    feature: 'chat',
  },

  // Agent Store
  {
    key: 'agent_store_view',
    displayName: 'Agent Store View',
    description: 'View agent store',
    category: 'core',
    feature: 'agent-store',
  },
  {
    key: 'agent_store_chat',
    displayName: 'Agent Store Chat',
    description: 'Chat with store agents',
    category: 'core',
    feature: 'agent-store',
  },

  // Workflow Store
  {
    key: 'workflow_store_view',
    displayName: 'Workflow Store View',
    description: 'View workflow store',
    category: 'core',
    feature: 'workflow-store',
  },
  {
    key: 'workflow_store_execute',
    displayName: 'Workflow Store Execute',
    description: 'Execute store workflows via API',
    category: 'core',
    feature: 'workflow-store',
  },

  // Agent Sandbox
  {
    key: 'agent_sandbox_view',
    displayName: 'Agent Sandbox View',
    description: 'View agent sandbox',
    category: 'interaction',
    feature: 'agent-sandbox',
  },
  {
    key: 'agent_sandbox_chat',
    displayName: 'Agent Sandbox Chat',
    description: 'Chat in sandbox',
    category: 'interaction',
    feature: 'agent-sandbox',
  },

  // Blueprints create/edit/delete (view flag lives in the ai_blueprints section above)
  {
    key: 'ai_blueprints_create',
    displayName: 'Blueprints Create',
    description: 'Create blueprint library entries and generate specifications',
    category: 'ai_blueprints',
    feature: 'ai-blueprints',
  },
  {
    key: 'ai_blueprints_edit',
    displayName: 'Blueprints Edit',
    description: 'Edit blueprints, submit for review, and approve',
    category: 'ai_blueprints',
    feature: 'ai-blueprints',
  },
  {
    key: 'ai_blueprints_delete',
    displayName: 'Blueprints Delete',
    description: 'Delete blueprint library entries',
    category: 'ai_blueprints',
    feature: 'ai-blueprints',
  },

  // Admin
  {
    key: 'admin_view',
    displayName: 'Admin View',
    description: 'Access admin panel',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_view',
    displayName: 'Admin Users View',
    description: 'View users',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_approve',
    displayName: 'Admin Users Approve',
    description: 'Approve users',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_reject',
    displayName: 'Admin Users Reject',
    description: 'Reject users',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_edit',
    displayName: 'Admin Users Edit',
    description: 'Edit users',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_delete',
    displayName: 'Admin Users Delete',
    description: 'Delete users',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_users_reset_password',
    displayName: 'Admin Users Reset Password',
    description: 'Reset user passwords',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_roles_view',
    displayName: 'Admin Roles View',
    description: 'View roles',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_roles_create',
    displayName: 'Admin Roles Create',
    description: 'Create roles',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_roles_edit',
    displayName: 'Admin Roles Edit',
    description: 'Edit roles',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'admin_roles_delete',
    displayName: 'Admin Roles Delete',
    description: 'Delete roles',
    category: 'administration',
    feature: 'admin',
  },

  // Platform Admin
  {
    key: 'platform_admin_view',
    displayName: 'Platform Admin View',
    description: 'Access platform admin',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_tenants_view',
    displayName: 'Platform Admin Tenants View',
    description: 'View tenants',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_tenants_create',
    displayName: 'Platform Admin Tenants Create',
    description: 'Create tenants',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_tenants_edit',
    displayName: 'Platform Admin Tenants Edit',
    description: 'Edit tenants',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_tenants_delete',
    displayName: 'Platform Admin Tenants Delete',
    description: 'Delete tenants',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_features_view',
    displayName: 'Platform Admin Features View',
    description: 'View feature flags',
    category: 'platform_admin',
    feature: 'platform-admin',
  },
  {
    key: 'platform_admin_features_edit',
    displayName: 'Platform Admin Features Edit',
    description: 'Edit feature flags',
    category: 'platform_admin',
    feature: 'platform-admin',
  },

  // Export/Import (general)
  {
    key: 'export_import_create',
    displayName: 'Export/Import Create',
    description: 'Create entities via import',
    category: 'administration',
    feature: 'admin',
  },
  {
    key: 'export_import_bundles',
    displayName: 'Export/Import Bundles',
    description: 'Export/import project bundles',
    category: 'agent_development',
    feature: 'projects',
  },
  // Export/Import entity-specific (distributed to respective features)
  {
    key: 'export_import_models',
    displayName: 'Export/Import Models',
    description: 'Export/import models',
    category: 'agent_ops',
    feature: 'models',
  },
  {
    key: 'export_import_tools',
    displayName: 'Export/Import Tools',
    description: 'Export/import tools',
    category: 'agent_ops',
    feature: 'tools',
  },
  {
    key: 'export_import_knowledge',
    displayName: 'Export/Import Knowledge',
    description: 'Export/import knowledge',
    category: 'agent_development',
    feature: 'knowledge',
  },
  {
    key: 'export_import_connectors',
    displayName: 'Export/Import Connectors',
    description: 'Export/import connectors',
    category: 'agent_ops',
    feature: 'connections',
  },
  {
    key: 'export_import_iam',
    displayName: 'Export/Import IAM',
    description: 'Export/import IAM configs',
    category: 'administration',
    feature: 'iam',
  },
  {
    key: 'export_import_prompts',
    displayName: 'Export/Import Prompts',
    description: 'Export/import prompts',
    category: 'agent_development',
    feature: 'prompts',
  },
  {
    key: 'export_import_skills',
    displayName: 'Export/Import Skills',
    description: 'Export/import skills',
    category: 'agent_development',
    feature: 'skills',
  },
  {
    key: 'export_import_agents',
    displayName: 'Export/Import Agents',
    description: 'Export/import agents',
    category: 'agent_development',
    feature: 'agent-studio',
  },
  {
    key: 'export_import_blueprints',
    displayName: 'Export/Import Blueprints',
    description: 'Export/import AI blueprints and their dependency bundles',
    category: 'agent_development',
    feature: 'ai-blueprints',
  },
  // Agent Evals (grouped under Evals and Simulations in Role permissions UI)
  {
    key: 'agent_evals_view',
    displayName: 'Agent Evals View',
    description: 'View evaluation sessions and results',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_create',
    displayName: 'Agent Evals Create',
    description: 'Create new evaluation sessions',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_edit',
    displayName: 'Agent Evals Edit',
    description: 'Edit evaluation sessions',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_delete',
    displayName: 'Agent Evals Delete',
    description: 'Delete evaluation sessions',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_templates_view',
    displayName: 'Eval Templates View',
    description: 'View evaluation templates',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_templates_create',
    displayName: 'Eval Templates Create',
    description: 'Create evaluation templates',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_templates_edit',
    displayName: 'Eval Templates Edit',
    description: 'Edit evaluation templates',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_templates_delete',
    displayName: 'Eval Templates Delete',
    description: 'Delete evaluation templates',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_custom_judge',
    displayName: 'Custom Judge',
    description: 'Use custom LLM-as-judge evaluation',
    category: 'observability',
    feature: 'evals-simulations',
  },
  {
    key: 'agent_evals_export',
    displayName: 'Evals Export',
    description: 'Export evaluation results',
    category: 'observability',
    feature: 'evals-simulations',
  },
]

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get all flag keys for a specific feature.
 */
export function getFlagKeysForFeature(feature: string): FeatureFlagKey[] {
  return FEATURE_FLAG_META.filter((meta) => meta.feature === feature).map((meta) => meta.key)
}

/**
 * Get all flag keys for a specific category.
 */
export function getFlagKeysForCategory(category: FeatureFlagCategory): FeatureFlagKey[] {
  return FEATURE_FLAG_META.filter((meta) => meta.category === category).map((meta) => meta.key)
}

/**
 * Get metadata for a specific flag key.
 */
export function getFlagMeta(key: FeatureFlagKey): FeatureFlagMeta | undefined {
  return FEATURE_FLAG_META.find((meta) => meta.key === key)
}

/**
 * Group flags by category for UI rendering.
 */
export function groupFlagsByCategory(): Record<FeatureFlagCategory, FeatureFlagMeta[]> {
  const groups: Record<FeatureFlagCategory, FeatureFlagMeta[]> = {
    core: [],
    agent_development: [],
    ai_blueprints: [],
    observability: [],
    agent_ops: [],
    interaction: [],
    administration: [],
    platform_admin: [],
  }

  for (const meta of FEATURE_FLAG_META) {
    groups[meta.category].push(meta)
  }

  return groups
}

/**
 * Check if a flag key is valid.
 *
 * A flag is valid if it's declared in FEATURE_FLAG_META OR exists as a node
 * in the role-access tree (every tree node owns a `{path}_view` flag that
 * isn't enumerated in FEATURE_FLAG_META).
 */
export function isValidFlagKey(key: string): key is FeatureFlagKey {
  if (FEATURE_FLAG_META.some((meta) => meta.key === key)) return true
  return TREE_VIEW_FLAG_KEYS.has(key)
}

/**
 * Whether a flag is effectively enabled for the current tenant.
 *
 * Fail-closed: a flag is enabled only if it is explicitly `true` in the API
 * payload. Flags absent from the payload are not deployed (master YAML disabled)
 * and are treated as disabled.
 */
export function isFlagEffectivelyEnabled(
  flagKey: string,
  flags: Record<string, boolean | undefined>,
): boolean {
  if (!isValidFlagKey(flagKey)) return false
  if (!isViewFlagEnabledWithAncestors(flagKey, flags)) return false
  return flags[flagKey] === true
}

/**
 * Get the view flag key for a feature.
 * Returns the {feature}_view flag key.
 */
export function getViewFlagForFeature(feature: string): FeatureFlagKey | undefined {
  const normalizedFeature = feature.replace(/-/g, '_')
  const viewKey = `${normalizedFeature}_view` as FeatureFlagKey
  return isValidFlagKey(viewKey) ? viewKey : undefined
}

/**
 * Create a default flags object with all flags disabled (fail-closed).
 * Actual permissions are loaded from the API; this prevents unauthorized
 * access if the flag fetch fails or hasn't completed yet.
 */
export function createDefaultFlags(): FeatureFlags {
  const flags: Record<string, boolean> = {}
  for (const meta of FEATURE_FLAG_META) {
    flags[meta.key] = meta.defaultEnabled ?? false
  }
  return flags as FeatureFlags
}

/**
 * Total count of feature flags.
 */
export const FEATURE_FLAG_COUNT = FEATURE_FLAG_META.length
